// PRD 11 §3.1–§3.3 — list orchestrator: filter bar (area/sub-area/search/
// per-page), table, loading/empty/no-match/error states, pagination, and the
// edit + category-manager slide panels.

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ClipboardX, Search } from 'lucide-react';

import { useAuthStore } from '@/app/store/auth.store';
import { Pagination } from '@/components/data/Pagination';
import { PAGE_SIZE_OPTIONS } from '@/shared/constants/pagination';
import { useDebouncedValue } from '@/shared/hooks/use-debounced-value';

import { useCategoryTreeQuery, useManualsQuery } from '../hooks/use-manuals';
import { useCounselingManualPageStore } from '../store/counseling-manual-page.store';
import type {
  ConsultationManual,
  ManualCategoryTree,
  ManualListParams,
} from '../types/counseling-manual.types';
import { getAreas, getSubAreas } from '../utils/category-tree';
import { DeleteManualConfirm } from './DeleteManualConfirm';
import { ManualFormPanel } from './ManualFormPanel';
import { ManualTable } from './ManualTable';

const EMPTY_TREE: ManualCategoryTree = { areas: [] };
// Same 300 ms as the Health Encyclopedia list — the two 참고 자료 lists share
// the real-time-search behaviour.
const SEARCH_DEBOUNCE_MS = 300;

function TableSkeleton() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 16 }}>
      {[0, 1, 2, 3, 4].map((i) => (
        <div
          key={i}
          className="animate-pulse"
          style={{ height: 40, background: 'var(--muted)', borderRadius: 'var(--radius-sm)' }}
        />
      ))}
    </div>
  );
}

function TableError({ message, onRetry }: { message: string; onRetry: () => void }) {
  const { t } = useTranslation('counseling-manual');
  return (
    <div
      role="alert"
      style={{
        margin: 16,
        padding: 16,
        border: '1px solid var(--destructive)',
        borderRadius: 'var(--radius-md)',
        background: '#FEE2E8',
        color: 'var(--destructive)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 16,
      }}
    >
      <span>{message}</span>
      <button type="button" className="btn btn-sm btn-outline" onClick={onRetry}>
        {t('list.retry')}
      </button>
    </div>
  );
}

function EmptyState({ title }: { title: string }) {
  return (
    <div className="table-empty">
      <i>
        <ClipboardX size={48} color="var(--muted-foreground)" />
      </i>
      <p className="table-empty__title">{title}</p>
    </div>
  );
}

export function CounselingManualView() {
  const { t } = useTranslation('counseling-manual');
  // PRD §3.7 — delete requires CONTENT_LIB:DELETE, seeded to Root only. The ✕ is
  // always rendered and disabled without it (PRD §8.3), so no WRITE check here.
  const canDelete = useAuthStore((s) => s.isRoot);

  const search = useCounselingManualPageStore((s) => s.search);
  const setSearch = useCounselingManualPageStore((s) => s.setSearch);
  const areaFilter = useCounselingManualPageStore((s) => s.areaFilter);
  const setAreaFilter = useCounselingManualPageStore((s) => s.setAreaFilter);
  const subAreaFilter = useCounselingManualPageStore((s) => s.subAreaFilter);
  const setSubAreaFilter = useCounselingManualPageStore((s) => s.setSubAreaFilter);
  const sortKey = useCounselingManualPageStore((s) => s.sortKey);
  const sortDir = useCounselingManualPageStore((s) => s.sortDir);
  const toggleSort = useCounselingManualPageStore((s) => s.toggleSort);
  const page = useCounselingManualPageStore((s) => s.page);
  const setPage = useCounselingManualPageStore((s) => s.setPage);
  const pageSize = useCounselingManualPageStore((s) => s.pageSize);
  const setPageSize = useCounselingManualPageStore((s) => s.setPageSize);
  const openEdit = useCounselingManualPageStore((s) => s.openEdit);

  const [deleteTarget, setDeleteTarget] = useState<ConsultationManual | null>(null);

  // SHCT-448 ① — search runs as-you-type with no explicit button, matching the
  // prototype (`searchInput.addEventListener('input', …)`) and the sibling
  // Health Encyclopedia list. The prototype filters in-memory; this list is
  // server-side (PRD §3.1), so the term is debounced to avoid one request per
  // keystroke. NOTE: PRD §3.3/§6.1 still describe "엔터 키 입력 또는 검색 버튼
  // 클릭" — stale wording, flagged for a PRD update.
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  const trimmedSearch = debouncedSearch.trim();

  // PRD §3.1 — server-side list (C2): all filter/sort/pagination is sent to the BE.
  const listParams = useMemo<ManualListParams>(
    () => ({
      area: areaFilter || undefined,
      subArea: subAreaFilter || undefined,
      keyword: trimmedSearch || undefined,
      sort: sortKey,
      direction: sortDir,
      page,
      size: pageSize,
    }),
    [areaFilter, subAreaFilter, trimmedSearch, sortKey, sortDir, page, pageSize],
  );

  const { data, isLoading, error, refetch } = useManualsQuery(listParams);
  const { data: tree = EMPTY_TREE } = useCategoryTreeQuery();

  const areas = useMemo(() => getAreas(tree), [tree]);
  const subAreas = useMemo(() => getSubAreas(tree, areaFilter), [tree, areaFilter]);

  const pageRows = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages, setPage]);

  const hasFilter = trimmedSearch.length > 0 || Boolean(areaFilter) || Boolean(subAreaFilter);

  return (
    <>
      <div className="filter-bar">
        <select
          className="filter-bar__select"
          value={areaFilter}
          onChange={(e) => setAreaFilter(e.target.value)}
          aria-label={t('filters.area')}
          style={{ width: 220 }}
        >
          <option value="">{t('filters.allAreas')}</option>
          {areas.map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </select>
        <select
          className="filter-bar__select"
          value={subAreaFilter}
          onChange={(e) => setSubAreaFilter(e.target.value)}
          disabled={!areaFilter}
          aria-label={t('filters.subArea')}
          style={{ width: 180 }}
        >
          <option value="">{t('filters.allSubAreas')}</option>
          {subAreas.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <div className="filter-bar__search" style={{ flex: 1 }}>
          <input
            type="text"
            className="filter-bar__search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('list.searchPlaceholder')}
            aria-label={t('list.searchPlaceholder')}
          />
          <Search size={16} className="filter-bar__search-icon" />
        </div>
        <select
          className="filter-bar__select"
          value={pageSize}
          aria-label={t('list.itemsPerPage', { count: pageSize })}
          onChange={(e) => setPageSize(Number(e.target.value))}
          style={{ marginLeft: 'auto' }}
        >
          {PAGE_SIZE_OPTIONS.map((n) => (
            <option key={n} value={n}>
              {t('list.itemsPerPage', { count: n })}
            </option>
          ))}
        </select>
      </div>

      <div className="card">
        {isLoading ? (
          <TableSkeleton />
        ) : error ? (
          <TableError message={t('list.loadError')} onRetry={() => void refetch()} />
        ) : (
          <>
            <ManualTable
              rows={pageRows}
              sortKey={sortKey}
              sortDir={sortDir}
              onSort={toggleSort}
              onRowOpen={(row) => openEdit(row.cid)}
              canDelete={canDelete}
              onDelete={setDeleteTarget}
            />
            {pageRows.length === 0 && (
              <EmptyState title={hasFilter ? t('list.emptyNoMatch') : t('list.empty')} />
            )}
          </>
        )}
      </div>
      <Pagination page={Math.min(page, totalPages)} totalPages={totalPages} onPageChange={setPage} />

      <DeleteManualConfirm manual={deleteTarget} onClose={() => setDeleteTarget(null)} />
      <ManualFormPanel />
    </>
  );
}
